import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import QRCodeStyling from "qr-code-styling"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import {
  Button,
  Container,
  Divider,
  Form,
  Icon,
  Input,
  Message,
  Modal,
  ModalActions,
  ModalContent,
} from "semantic-ui-react"

import actions from "actions"
import api from "api"
import { SuggestedMFAApps } from "components/lib/Entry/EnforceMFA"
import PasswordGuidance from "components/lib/Entry/PasswordGuidance"
import AppIcon from "src/images/appIcon.png"
import { useAppDispatch, useAppSelector } from "store"
import util from "utils/utils"

export const UserSettingsPassword = () => {
  const user = useAppSelector((state) => state.user)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const onEnabledMfa = useCallback(() => {
    dispatch(actions.user.enableMfa())
  }, [dispatch])
  const onDisabledMfa = useCallback(() => {
    dispatch(actions.user.disableMfa())
  }, [dispatch])

  const [errorMessage, setErrorMessage] = useState("")
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [working, setWorking] = useState(false)
  const [showPasswordOld, setShowPasswordOld] = useState(false)
  const [showPasswordNew, setShowPasswordNew] = useState(false)
  const [mfaLoading, setMfaLoading] = useState(false)
  const [mfa, setMfa] = useState(user.mfaEnabled)
  const [uri, setUri] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [disableMfaModel, setDisableMfaModel] = useState(false)
  const [mfaPassword, setMfaPassword] = useState("")
  const [otpVerifier, setOtpVerifier] = useState("")
  const otpQrRef = useRef(null)

  const updatePassword = () => {
    setWorking(true)
    api.auth.updatePassword(
      currentPassword,
      newPassword,
      () => {
        toast.success(t("users.account.options.changePassword.success"))
        setCurrentPassword("")
        setNewPassword("")
        setWorking(false)
        setErrorMessage("")
      },
      (err) => {
        setWorking(false)
        setErrorMessage(err.message)
      },
    )
  }

  const qrCode = useMemo(
    () =>
      new QRCodeStyling({
        width: 200,
        height: 200,
        type: "svg",
        data: uri,
        image: AppIcon,
        dotsOptions: {
          color: "#14435B",
        },
        backgroundOptions: {
          color: "#ffffff",
        },
        imageOptions: {
          margin: 4,
        },
      }),
    [uri],
  )

  useEffect(() => {
    if (modalOpen && !qrCode._container) {
      qrCode.append(otpQrRef.current)
    }
  }, [modalOpen, qrCode])

  const toggleMfa = useCallback(() => {
    const shouldToggle = !mfa
    if (shouldToggle) {
      setModalOpen(true)
      api.auth.generateOtpUri(
        (check) => {
          setUri(check.uri)
        },
        (err) => {
          toast.error(err.message)
        },
      )
    } else {
      setDisableMfaModel(true)
    }
  }, [mfa])

  const disableMfa = () => {
    api.auth.disableOtp(
      { password: mfaPassword },
      () => {
        toast.success(t("entry.mfa.statusMessageDisabledSuccess"))
        setMfa(false)
        onDisabledMfa()
      },
      (err) => {
        toast.error(err.message)
      },
    )
    setDisableMfaModel(false)
    setMfaPassword("")
  }

  const checkMfaCode = () => {
    const loadingId = toast.loading(t("entry.mfa.statusMessageLoading"))
    setMfaLoading(true)
    api.auth.enableOtp(
      { otp: otpVerifier },
      () => {
        setMfaLoading(false)
        setModalOpen(false)
        setOtpVerifier("")
        toast.success(t("entry.mfa.statusMessageSuccess"), { id: loadingId })
        dispatch(actions.user.enableMfa())
      },
      (err) => {
        setMfaLoading(false)
        toast.error(err.message, { id: loadingId })
        setOtpVerifier("")
      },
    )
  }

  return (
    <Container>
      <h3>{t("users.account.options.changePassword.title")}</h3>
      <p style={{ marginBottom: 0 }}>{t("users.account.options.changePassword.info")}</p>
      <PasswordGuidance password={newPassword} />
      <Form>
        <Form.Field>
          <label htmlFor="current-password-input">{t("users.account.options.changePassword.currentPass")}</label>
          <Form.Input
            id="current-password-input"
            style={{ width: 300 }}
            type={showPasswordOld ? "text" : "password"}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            icon={{
              name: showPasswordOld ? "eye slash" : "eye",
              circular: true,
              link: true,
              onClick: () => setShowPasswordOld(!showPasswordOld),
            }}
          />
        </Form.Field>
        <Form.Field>
          <label htmlFor="new-password-input">{t("users.account.options.changePassword.newPass")}</label>
          <Form.Input
            id="new-password-input"
            style={{ width: 300 }}
            type={showPasswordNew ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            icon={{
              name: showPasswordNew ? "eye slash" : "eye",
              circular: true,
              link: true,
              onClick: () => setShowPasswordNew(!showPasswordNew),
            }}
          />
        </Form.Field>
      </Form>
      {errorMessage && !working && (
        <Message style={{ width: "50%", marginBottom: 0 }} size="small" negative>
          <Icon name="warning circle" />
          {errorMessage}.
        </Message>
      )}
      <Button
        style={{ marginTop: "15px", maxWidth: "fit-content" }}
        primary
        icon="check"
        content={t("users.account.options.changePassword.change")}
        onClick={() => updatePassword()}
        loading={working}
      />

      <Divider hidden />
      <h3>
        <Icon name="lock" style={{ paddingRight: "10px" }} />
        {t("entry.mfa.title")}
      </h3>
      <p>{t("entry.mfa.explainer")}</p>
      <Message style={{ marginTop: 0 }}>
        <SuggestedMFAApps />
      </Message>

      <Button
        primary
        style={{ maxWidth: "fit-content" }}
        icon={!mfa ? "lock" : "unlock"}
        content={mfa ? t("entry.mfa.disableAction") : t("entry.mfa.enableAction")}
        onClick={toggleMfa}
      />

      <Modal mountNode={document.getElementById("semantic-modal-mount-node")} open={modalOpen}>
        <Modal.Header>
          <Icon name="lock" style={{ paddingRight: "5px" }} />
          {t("entry.mfa.enableAction")}
        </Modal.Header>
        <Modal.Content scrolling>
          <h3>{t("generic.step")} 1</h3>
          <p>{t("entry.mfa.step1")}</p>
          <Message info>
            <SuggestedMFAApps />
          </Message>
          <h3>{t("generic.step")} 2</h3>
          <p>{t("entry.mfa.step2")}</p>
          <div style={{ width: "70%", margin: "10px auto", textAlign: "center" }} ref={otpQrRef} />
          <h3>{t("generic.step")} 3</h3>
          <p>{t("entry.mfa.step3")}</p>
          <div style={{ textAlign: "center" }}>
            <Input
              onKeyDown={(e) => {
                e.keyCode === 13 && checkMfaCode()
              }}
              size="large"
              onChange={(e) => setOtpVerifier(e.target.value)}
              value={otpVerifier}
              placeholder="123456"
              autoFocus
            />
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button
            content={t("generic.cancel")}
            onClick={() => {
              setModalOpen(false)
            }}
          />
          <Button
            primary
            content={t("generic.verify")}
            loading={mfaLoading}
            onClick={() => checkMfaCode()}
            disabled={otpVerifier.replaceAll(" ", "").length !== 6}
          />
        </Modal.Actions>
      </Modal>
      <Modal mountNode={document.getElementById("semantic-modal-mount-node")} open={disableMfaModel}>
        <Modal.Header>
          <Icon name="lock" style={{ paddingRight: "5px" }} />
          {t("entry.mfa.disableAction")}
        </Modal.Header>
        <ModalContent>
          {user.ownerOrganisation?.mfaEnforcing ? (
            <Message>
              {t("entry.mfa.disabledWhenEnforcedMessage", {
                orgName: user.ownerOrganisation?.name,
              })}
            </Message>
          ) : null}
          <p>{t("entry.mfa.reenableSecurityMessage")}</p>
          <p>{t("entry.mfa.requirePassword")}</p>
          <div style={{ textAlign: "center" }}>
            <Input
              size="large"
              onKeyDown={(e) => {
                e.keyCode === 13 && disableMfa()
              }}
              type="password"
              onChange={(e) => setMfaPassword(e.target.value)}
              value={mfaPassword}
              autoFocus
            />
          </div>
        </ModalContent>
        <ModalActions>
          <Button content={t("generic.cancel")} onClick={() => setDisableMfaModel(false)} />
          <Button primary content={t("generic.verify")} onClick={() => disableMfa()} />
        </ModalActions>
      </Modal>
    </Container>
  )
}

export default UserSettingsPassword
